import { QUnitType, OpUnitType, Dayjs } from 'dayjs';

export type FormatTypeConfig = {
  LT: string;
  LTS: string;
  lt: string;
  lts: string;
  L: string;
  LL: string;
  'LL-D': string;
  'LL-Y': string;
  LLL: string;
  'LLL-Y': string;
  LLLL: string;
  'LLLL-Y': string;
  l: string;
  'l-Y': string;
  ll: string;
  'll-Y': string;
  'll-D': string;
  lll: string;
  'lll-Y': string;
  llll: string;
  'llll-Y': string;
  'll-Y+w': string;
  'LT+to': string;
  'LLL+to': string;
};

export type FormatType = keyof FormatTypeConfig;

export type TimeRangeFormatTypeConfig = {
  yl: string;
  'yl-1': string;
  ml: string;
  'ml-1': string;
  dl: string;
  y: string;
  'y-1': string;
  m: string;
  'm-1': string;
  d: string;
  Y: string;
  'Y-1': string;
  M: string;
  D: string;
  yt: string;
  mt: string;
  dt: string;
  t: string;
};

export type TimeRangeFormatType = keyof TimeRangeFormatTypeConfig;

export type LocaleName =
  | 'af'
  | 'ar'
  | 'az'
  | 'bg'
  | 'bn'
  | 'ca'
  | 'ceb'
  | 'cs'
  | 'da'
  | 'de'
  | 'el'
  | 'en-GB'
  | 'en'
  | 'es-419'
  | 'es'
  | 'et'
  | 'fa'
  | 'fi'
  | 'fil'
  | 'fr-CA'
  | 'fr'
  | 'ga'
  | 'gu'
  | 'he'
  | 'hi'
  | 'hr'
  | 'hu'
  | 'id'
  | 'is'
  | 'it'
  | 'ja'
  | 'jv'
  | 'kk'
  | 'km'
  | 'kn'
  | 'ko'
  | 'lt'
  | 'lv'
  | 'ml'
  | 'mr'
  | 'ms'
  | 'my'
  | 'nb'
  | 'nl'
  | 'or'
  | 'pa'
  | 'pl'
  | 'pt-BR'
  | 'pt-PT'
  | 'pt'
  | 'ro'
  | 'ru'
  | 'sk'
  | 'sl'
  | 'sq'
  | 'sv'
  | 'sw'
  | 'ta'
  | 'te'
  | 'th'
  | 'tr'
  | 'uk'
  | 'ur'
  | 'uz'
  | 'vi'
  | 'zh-Hans'
  | 'zh-Hant';

export type LocaleConfig = {
  name: LocaleName;
  months: string[];
  monthsShort: string[];
  weekdays: string[];
  weekdaysShort: string[];
  weekdaysMin: string[];
  longDateFormat: FormatTypeConfig;
  meridiem?: {
    am: string;
    pm: string;
    AM: string;
    PM: string;
  };
  ordinal: OrdinalFunc;
  timePref: Record<string, string>;
};

export type MeridiemFunc = (hour: number, minute: number, isLower: boolean) => string;

export type DateOrTimestamp = Date | number;

export type TimeDiffType = 'abbr' | 'full';

export type RelativeTimeType = 'normal' | 'long';

export type RelativeTimeUnit = 's' | 'm' | 'h' | 'd' | 'w' | 'M' | 'y';

export type RelativeTimePluralType = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';

export type RelativeTimeTemplateMap = {
  [key: string]: string;
};

export type RelativeTimeKeyToTemplateMap = {
  [key in RelativeTimeUnit]: RelativeTimeTemplateMap;
};

export type TimeRangeTemplateMap = {
  start: string;
  end: string;
};

export type TimeRangeFormatKeyToTemplateMap = {
  [key in TimeRangeFormatType]: TimeRangeTemplateMap;
};

export type OrdinalFunc = (number: number, period?: string) => string;

// TODO: Replace LocaleConfig once refactor is done
export interface TransformedLocaleConfig {
  name: LocaleName;
  months: string[];
  monthsShort: string[];
  weekdays: string[];
  weekdaysShort: string[];
  weekdaysMin: string[];
  formats: FormatTypeConfig;
  meridiem: MeridiemFunc;
  future: RelativeTimeKeyToTemplateMap;
  past: RelativeTimeKeyToTemplateMap;
  abbr: RelativeTimeKeyToTemplateMap;
  justNow: string;
  yesterday: string;
  today: string;
  tomorrow: string;
  weekStart: number;
  timeRange: string;
  timeZone: string;
  timePref: Record<string, string>;
  timeRangeStyles: TimeRangeFormatKeyToTemplateMap;
  ordinal: OrdinalFunc;
}

export type RelativeTimeInput = string | number | Date | Dayjs;

export type RelativeTimeThreshold = {
  relativeTimeKey: RelativeTimeUnit;
  num: number;
  unit: QUnitType | OpUnitType;
};

export type RelativeTimeOptions = {
  rounding: (num: number) => number;
  thresholds: RelativeTimeThreshold[];
};

export type TimeExpressionOptions = {
  justNowThreshold: number;
};

declare module 'dayjs' {
  interface Dayjs {
    $locale: () => TransformedLocaleConfig;
    to: (input: RelativeTimeInput, useAbbreviation?: boolean) => string;
    getTimeExpression: (start: DateOrTimestamp, end: DateOrTimestamp) => string;
  }
}

export type DisplayType = 'dayOfTheWeek' | 'date' | 'time' | 'year' | 'zone' | 'month';

export type TUXTimeFormatOptions = {
  // [1] What to display (required)
  display: Set<DisplayType>; // ['dayOfTheWeek', 'date', 'time', 'year', 'zone']

  // [2] How to format it
  notation?: 'mid' | 'long';

  // [3] Relative time rules
  relative?: boolean; // show "2 min ago", "2 days ago" for date and time.

  // [4] Special substitutions
  useStrings?: boolean;
  dayNamesThisWeek?: boolean; // Show "Tuesday" for same-week dates
  showTimeToday?: boolean; // Show "3:00 PM" within 24h
};

export type OptionalTUXTimeFormatOptions = Omit<TUXTimeFormatOptions, 'display'>;

export type RangeDisplayType = 'dayOfTheWeek' | 'date' | 'time' | 'year';

export type TUXTimeRangeFormatOptions = {
  display: Set<RangeDisplayType>;
  notation?: 'mid' | 'long';
};

export type OptionalTUXTimeRangeFormatOptions = Omit<TUXTimeRangeFormatOptions, 'display'>;

export const DEFAULT_TUX_TIME_FORMAT_OPTIONS: OptionalTUXTimeFormatOptions = {
  notation: 'mid',
  relative: true,
  useStrings: true,
  dayNamesThisWeek: false,
  showTimeToday: true,
};

export const DEFAULT_TUX_TIME_RANGE_FORMAT_OPTIONS: OptionalTUXTimeRangeFormatOptions = {
  notation: 'mid',
};
